import { Crown, Medal, Search, Trophy } from 'lucide-react';
import { useState } from 'react';
import type { PickemUserScore } from '@api/generated/types.gen';
import { cn } from '@/lib/utils';
import { UserAvatar } from './user-avatar';

interface PickemLeaderboardProps {
  entries: PickemUserScore[];
  currentUserId?: string;
  maxEntries?: number;
}

type RankedEntry = PickemUserScore & { rank: number };

const rankColor = (rank: number) => {
  if (rank === 1) return 'text-amber-400';
  if (rank === 2) return 'text-slate-300';
  if (rank === 3) return 'text-orange-400';
  return 'text-muted-foreground';
};

const podiumTheme = (rank: number) => {
  if (rank === 1)
    return {
      border: 'border-amber-500/40',
      gradient: 'from-amber-500/15 via-amber-500/5 to-transparent',
      accent: 'text-amber-400',
      glow: 'shadow-[0_0_24px_-8px_rgba(251,191,36,0.5)]',
    };
  if (rank === 2)
    return {
      border: 'border-slate-400/40',
      gradient: 'from-slate-400/15 via-slate-400/5 to-transparent',
      accent: 'text-slate-300',
      glow: '',
    };
  return {
    border: 'border-orange-500/40',
    gradient: 'from-orange-500/15 via-orange-500/5 to-transparent',
    accent: 'text-orange-400',
    glow: '',
  };
};

export function PickemLeaderboard({
  entries,
  currentUserId,
  maxEntries,
}: PickemLeaderboardProps) {
  const [query, setQuery] = useState('');

  const sliced = maxEntries ? entries.slice(0, maxEntries) : entries;

  if (sliced.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Trophy className="h-10 w-10 text-muted-foreground/30 mb-3" />
        <p className="text-sm font-medium">No one's on the board yet</p>
        <p className="text-xs text-muted-foreground mt-1">
          Be the first to submit picks and claim the top spot.
        </p>
      </div>
    );
  }

  const ranked: RankedEntry[] = sliced.map((entry, idx) => ({
    ...entry,
    rank: idx + 1,
  }));
  const leaderPoints = ranked[0]?.totalPoints ?? 0;
  const avgPoints = Math.round(
    ranked.reduce((s, e) => s + e.totalPoints, 0) / ranked.length,
  );

  const podium = ranked.slice(0, 3);
  const rest = ranked.slice(3);

  const trimmedQuery = query.trim().toLowerCase();
  const filteredRest = trimmedQuery
    ? rest.filter((e) => {
        const name = (e.displayName ?? e.username ?? e.userId).toLowerCase();
        return name.includes(trimmedQuery);
      })
    : rest;

  const currentUserEntry = currentUserId
    ? ranked.find((e) => e.userId === currentUserId)
    : null;
  const currentUserInPodium =
    !!currentUserEntry &&
    podium.some((e) => e.userId === currentUserEntry.userId);
  const currentUserInFiltered =
    !!currentUserEntry &&
    filteredRest.some((e) => e.userId === currentUserEntry.userId);
  const showStickyCurrentUser =
    !!currentUserEntry && !currentUserInPodium && !currentUserInFiltered;

  return (
    <div className="h-full flex flex-col min-h-0 gap-4">
      <div className="grid grid-cols-3 gap-2 shrink-0">
        <StatPill label="Players" value={ranked.length.toString()} />
        <StatPill label="Leader" value={leaderPoints.toString()} hint="pts" />
        <StatPill label="Average" value={avgPoints.toString()} hint="pts" />
      </div>

      {podium.length >= 3 ? (
        <div className="shrink-0">
          <Podium entries={podium} currentUserId={currentUserId} />
        </div>
      ) : (
        <div className="space-y-1 shrink-0">
          {podium.map((entry) => (
            <LeaderboardRow
              key={entry.userId}
              entry={entry}
              leaderPoints={leaderPoints}
              isCurrentUser={entry.userId === currentUserId}
            />
          ))}
        </div>
      )}

      {rest.length > 0 && (
        <div className="relative shrink-0">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search players..."
            className="w-full h-8 pl-8 pr-2 text-xs rounded-md border border-border bg-card focus:outline-none focus:ring-1 focus:ring-primary transition-shadow"
          />
        </div>
      )}

      {rest.length > 0 && (
        <div className="flex-1 min-h-0 overflow-y-auto -mr-1 pr-1">
          <div className="space-y-1">
            {filteredRest.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-6">
                No players match "{query}".
              </p>
            ) : (
              filteredRest.map((entry) => (
                <LeaderboardRow
                  key={entry.userId}
                  entry={entry}
                  leaderPoints={leaderPoints}
                  isCurrentUser={entry.userId === currentUserId}
                />
              ))
            )}
          </div>

          {showStickyCurrentUser && currentUserEntry && (
            <div className="sticky bottom-0 pt-2 pb-0.5 bg-gradient-to-t from-background via-background to-transparent">
              <div className="rounded-md border-2 border-primary/50 bg-card shadow-lg">
                <LeaderboardRow
                  entry={currentUserEntry}
                  leaderPoints={leaderPoints}
                  isCurrentUser
                  hideBorder
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Podium({
  entries,
  currentUserId,
}: {
  entries: RankedEntry[];
  currentUserId?: string;
}) {
  const [first, second, third] = entries;
  const order: (RankedEntry | undefined)[] = [second, first, third];

  // Inner padding gives the absolutely-positioned crowns (-top-3), "You"
  // badges (-top-2), and ring-offset rings (4px each side) clearance inside
  // the TabsContent's `overflow-hidden` clip rect — otherwise they get cut on
  // narrow mobile viewports.
  return (
    <div className="grid grid-cols-3 gap-2 items-end pt-5 px-1">
      {order.map((entry, idx) =>
        entry ? (
          <PodiumCard
            key={entry.userId}
            entry={entry}
            isCurrentUser={entry.userId === currentUserId}
          />
        ) : (
          <div key={`empty-${idx}`} />
        ),
      )}
    </div>
  );
}

function PodiumCard({
  entry,
  isCurrentUser,
}: {
  entry: RankedEntry;
  isCurrentUser: boolean;
}) {
  const name = entry.displayName ?? entry.username ?? entry.userId;
  const theme = podiumTheme(entry.rank);
  const isFirst = entry.rank === 1;
  const accuracy =
    entry.totalPicks > 0
      ? Math.round((entry.correctPicks / entry.totalPicks) * 100)
      : 0;

  return (
    <div
      className={cn(
        'relative flex flex-col items-center gap-1.5 p-3 pt-5 rounded-lg border bg-gradient-to-b',
        theme.border,
        theme.gradient,
        theme.glow,
        isFirst && 'py-4 pt-6',
        isCurrentUser &&
          'ring-2 ring-primary ring-offset-2 ring-offset-background',
      )}
    >
      <div className="absolute -top-3 left-1/2 -translate-x-1/2 flex items-center justify-center">
        {isFirst ? (
          <Crown className={cn('h-6 w-6 drop-shadow', theme.accent)} />
        ) : (
          <Medal className={cn('h-5 w-5 drop-shadow', theme.accent)} />
        )}
      </div>
      <UserAvatar
        profilePictureUrl={entry.profilePictureUrl ?? null}
        displayName={name}
        size={isFirst ? 'md' : 'sm'}
      />
      <div className="min-w-0 w-full text-center">
        <p
          className={cn(
            'truncate',
            isFirst ? 'text-sm font-bold' : 'text-xs font-semibold',
          )}
        >
          {name}
        </p>
        <p className="text-[10px] text-muted-foreground tabular-nums">
          {entry.correctPicks}/{entry.totalPicks} · {accuracy}%
        </p>
      </div>
      <p
        className={cn(
          'font-bold tabular-nums',
          isFirst ? 'text-xl' : 'text-base',
          theme.accent,
        )}
      >
        {entry.totalPoints}
        <span className="text-[10px] text-muted-foreground ml-0.5 font-normal">
          pts
        </span>
      </p>
      {isCurrentUser && (
        <span className="absolute -top-2 right-2 text-[9px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded-full bg-primary text-primary-foreground">
          You
        </span>
      )}
    </div>
  );
}

function LeaderboardRow({
  entry,
  leaderPoints,
  isCurrentUser,
  hideBorder,
}: {
  entry: RankedEntry;
  leaderPoints: number;
  isCurrentUser: boolean;
  hideBorder?: boolean;
}) {
  const name = entry.displayName ?? entry.username ?? entry.userId;
  const accuracy =
    entry.totalPicks > 0
      ? Math.round((entry.correctPicks / entry.totalPicks) * 100)
      : 0;
  const pointsPct =
    leaderPoints > 0 ? (entry.totalPoints / leaderPoints) * 100 : 0;

  return (
    <div
      className={cn(
        'grid grid-cols-[28px_1fr_auto] gap-3 items-center px-3 py-2 rounded-md transition-colors',
        !hideBorder && 'border border-transparent hover:border-border/60',
        isCurrentUser && !hideBorder && 'bg-primary/5 border-primary/30',
      )}
    >
      <span
        className={cn(
          'text-center text-sm font-mono tabular-nums',
          rankColor(entry.rank),
        )}
      >
        {entry.rank}
      </span>
      <div className="flex items-center gap-2.5 min-w-0">
        <UserAvatar
          profilePictureUrl={entry.profilePictureUrl ?? null}
          displayName={name}
          size="sm"
        />
        <div className="min-w-0 flex-1">
          <p
            className={cn(
              'text-sm truncate',
              isCurrentUser ? 'font-semibold' : 'font-medium',
            )}
          >
            {name}
            {isCurrentUser && (
              <span className="ml-1.5 text-[9px] text-primary uppercase tracking-wide font-semibold">
                You
              </span>
            )}
          </p>
          <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <span className="tabular-nums">
              {entry.correctPicks}/{entry.totalPicks}
            </span>
            <span className="text-muted-foreground/40">·</span>
            <span className="tabular-nums">{accuracy}%</span>
          </div>
        </div>
      </div>
      <div className="flex flex-col items-end gap-1 min-w-[60px]">
        <span className="text-sm font-semibold tabular-nums leading-none">
          {entry.totalPoints}
          <span className="text-[10px] text-muted-foreground ml-0.5 font-normal">
            pts
          </span>
        </span>
        <div className="h-1 w-14 rounded-full bg-muted overflow-hidden">
          <div
            className={cn(
              'h-full rounded-full transition-all duration-500',
              isCurrentUser ? 'bg-primary' : 'bg-primary/50',
            )}
            style={{ width: `${Math.max(4, pointsPct)}%` }}
          />
        </div>
      </div>
    </div>
  );
}

function StatPill({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="bg-card border rounded-md px-3 py-2 text-center">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <p className="text-sm font-bold tabular-nums mt-0.5">
        {value}
        {hint && (
          <span className="text-[10px] text-muted-foreground ml-1 font-normal">
            {hint}
          </span>
        )}
      </p>
    </div>
  );
}
